/**
 * /well-testing/tickets/:id — one Well Testing field ticket (fieldiq-api#1253,
 * WT-4). Pipeline & Facility's ticket screen, carried over: this vertical's API
 * mirrors PF-4, plus `void`.
 *
 * WHAT MAY BE EDITED (wtTicketEditMode, mirroring the API):
 *   draft              lines and header edit freely.
 *   office_approved,   an edit returns the ticket to draft. The screen does
 *   customer_rejected  not offer the edit until the office says "unlock" to a
 *                      sentence saying so; when a write then comes back
 *                      `reverted_to_draft: true` the screen says that too.
 *   customer_approved  frozen. Only the internal notes change.
 *   void               frozen entirely, notes included; it says who voided it
 *                      and why. Void is offered from every other status, with
 *                      a required reason, and is confirmed inline.
 *
 * Refresh from the shift logs redraws the 'auto' lines and says FIRST that an
 * office price on one of them is lost (office lines are kept). The PDF is
 * offered once the office has signed (a draft or void ticket is 409). No
 * modals: every confirm is inline and every refusal is a sentence in the
 * refusal style.
 *
 * Preflight check (WT-6) is offered to the AI review roles on a draft, office-
 * approved or customer-rejected ticket only (wtTicketPreflightable) — never on a
 * customer-approved or void one, which the API refuses with a 409. The result
 * shows in place as a draft awaiting approval.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Ban, FileText, Lock, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, getBlobApiErrorMessage } from '../../../api/client';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useWtMeta } from '../../../hooks/useWellTesting';
import { useDeleteWtTicket, useWtRates, useWtTicket, useRefreshWtTicket, useUpdateWtTicket, useVoidWtTicket, type WtTicketEditResult } from '../../../hooks/useWtTickets';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../../lib/blob-tab';
import { formatInstant } from '../../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import {
  canWriteWtTickets,
  wtScopeText,
  wtTicketDeletable,
  wtTicketEditMode,
  wtTicketHasPdf,
  wtTicketInvoicedFrom,
  wtTicketRefusal,
  wtTicketStatusSkin,
  wtTicketWellText,
  WT_REFRESH_WARNING,
  WT_VOID_WARNING,
} from '../../../lib/wt-tickets';
import { blankToNull } from '../../../lib/well-testing';
import { canUseWtAi, wtAiRefusal, wtTicketPreflightable } from '../../../lib/wt-ai';
import { useRunWtAi } from '../../../hooks/useWtAi';
import type { WtAiRunResult } from '../../../types/wt-ai';
import { WtAiRunInPlace, WtPreflightButton } from '../ai/WtAiBits';
import type { WtTicket, WtTicketCatalog, WtTicketHeaderWrite } from '../../../types/wt-tickets';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, WT_EYEBROW, WT_INPUT, WT_PRIMARY, WT_SECONDARY, WtBadge, WtEmpty, WtField, WtFormError, WtLoading, WtPanel, WtSpec } from '../WtBits';
import { PfInlineConfirm as WtInlineConfirm } from '../../pipeline-facility/digs/DigBits';
import { WtRevertedNotice } from './TicketBits';
import { WtTicketLines } from './TicketLines';
import { WtTicketSignOff } from './TicketSignOff';
import { WtTicketInvoiceLink } from '../invoices/InvoiceBits';

const HEADER_FIELDS: { key: keyof WtTicketHeaderWrite; read: keyof WtTicket; label: string; max: number }[] = [
  { key: 'afe_number', read: 'afeNumber', label: 'AFE number', max: 100 },
  { key: 'po_number', read: 'poNumber', label: 'PO number', max: 255 },
  { key: 'cost_code', read: 'costCode', label: 'Cost code', max: 100 },
];

function HeaderForm({ ticket, pending, error, onCancel, onSubmit }: {
  ticket: WtTicket;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (body: WtTicketHeaderWrite) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = { customer_notes: ticket.customerNotes ?? '' };
    HEADER_FIELDS.forEach((f) => { v[f.key] = (ticket[f.read] as string | null) ?? ''; });
    return v;
  });
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setValues((v) => ({ ...v, [k]: e.target.value }));
  return (
    <div data-testid="wt-ticket-header-form" className="space-y-3 border-t border-fieldiq-border px-4 py-4">
      <WtFormError message={error} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {HEADER_FIELDS.map((f) => (
          <WtField key={f.key} label={f.label}>
            <input className={WT_INPUT} maxLength={f.max} value={values[f.key]} onChange={set(f.key)} />
          </WtField>
        ))}
      </div>
      <WtField label="Notes for the customer" hint="Printed on the ticket.">
        <textarea className={`${WT_INPUT} h-24 py-2`} maxLength={5000} value={values.customer_notes} onChange={set('customer_notes')} />
      </WtField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WT_SECONDARY} onClick={onCancel} disabled={pending}>Cancel</button>
        <button
          type="button"
          className={WT_PRIMARY}
          disabled={pending}
          onClick={() => {
            const body: WtTicketHeaderWrite = {};
            const cur: Record<string, string | null> = { customer_notes: ticket.customerNotes };
            HEADER_FIELDS.forEach((f) => { cur[f.key] = ticket[f.read] as string | null; });
            Object.keys(values).forEach((k) => {
              const next = blankToNull(values[k]);
              if (next !== (cur[k] ?? null)) (body as Record<string, string | null>)[k] = next;
            });
            onSubmit(body);
          }}
        >
          {pending ? 'Saving…' : 'Save details'}
        </button>
      </div>
    </div>
  );
}

/**
 * Void: a required reason, said before the office presses it. 400 `reason_required` still reads as a sentence.
 * A ticket on a live invoice is refused (409 `ticket_invoiced`, WT-5a): the sentence names the invoice and
 * the page links to it, since the invoice is what has to be voided first.
 */
function VoidTicket({ ticket, onCancel, onDone }: { ticket: WtTicket; onCancel: () => void; onDone: () => void }) {
  const voidTicket = useVoidWtTicket();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [onInvoice, setOnInvoice] = useState<{ id: string; number: string } | null>(null);
  return (
    <div data-testid="wt-ticket-void" role="group" aria-label="Void this ticket" className="space-y-3 rounded-fi-control border border-fi-red/50 bg-fi-signal-red/10 px-4 py-4">
      <p className="text-sm text-fieldiq-text">{WT_VOID_WARNING}</p>
      <WtFormError message={error} />
      {onInvoice && (
        <p className="text-sm text-fieldiq-text" data-testid="wt-ticket-void-invoice">
          Open{' '}
          <Link className="font-mono font-bold underline" to={`/well-testing/invoices/${onInvoice.id}`}>
            {onInvoice.number}
          </Link>{' '}
          to void it.
        </p>
      )}
      <WtField label="Why is it being voided? *">
        <textarea className={`${WT_INPUT} h-20 py-2`} maxLength={2000} aria-label="Reason for voiding" value={reason} onChange={(e) => setReason(e.target.value)} />
      </WtField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WT_SECONDARY} onClick={onCancel} disabled={voidTicket.isPending}>Keep it</button>
        <button
          type="button"
          className={WT_PRIMARY}
          disabled={voidTicket.isPending}
          onClick={() => {
            setError(null);
            setOnInvoice(null);
            if (!reason.trim()) return setError('Say why the ticket is being voided.');
            voidTicket.mutate(
              { id: ticket.id, reason: reason.trim() },
              {
                onSuccess: () => { toast.success(`${ticket.ticketNumber} voided`); onDone(); },
                onError: (e) => {
                  setError(wtTicketRefusal(e, 'The ticket could not be voided.'));
                  setOnInvoice(wtTicketInvoicedFrom(e));
                },
              },
            );
          }}
        >
          {voidTicket.isPending ? 'Voiding…' : 'Void ticket'}
        </button>
      </div>
    </div>
  );
}

function InternalNotes({ ticket, canWrite }: { ticket: WtTicket; canWrite: boolean }) {
  const update = useUpdateWtTicket();
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(ticket.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  return (
    <WtPanel
      eyebrow="Internal"
      title="Office notes"
      testId="wt-ticket-notes"
      actions={canWrite && ticket.status !== 'void' && !editing ? <button type="button" className={WT_SECONDARY} onClick={() => { setNotes(ticket.notes ?? ''); setError(null); setEditing(true); }}>Edit notes</button> : undefined}
    >
      {editing ? (
        <div className="space-y-3 px-4 py-4">
          <WtFormError message={error} />
          <WtField label="Notes" hint="Never printed on the ticket, so they can change at any status but void.">
            <textarea className={`${WT_INPUT} h-24 py-2`} maxLength={5000} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </WtField>
          <div className="flex justify-end gap-2">
            <button type="button" className={WT_SECONDARY} onClick={() => setEditing(false)} disabled={update.isPending}>Cancel</button>
            <button
              type="button"
              className={WT_PRIMARY}
              disabled={update.isPending}
              onClick={() =>
                update.mutate(
                  { id: ticket.id, body: { notes: blankToNull(notes) } },
                  { onSuccess: () => { toast.success('Notes saved'); setEditing(false); }, onError: (e) => setError(wtTicketRefusal(e, 'The notes could not be saved.')) },
                )
              }
            >
              {update.isPending ? 'Saving…' : 'Save notes'}
            </button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap px-4 py-4 text-sm text-fieldiq-text">{ticket.notes || '—'}</p>
      )}
    </WtPanel>
  );
}

export default function WtTicketDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/well-testing/tickets');
  const { user } = useAuth();
  const canWrite = canWriteWtTickets(user?.role);
  const query = useWtTicket(id);
  const catalog = (useWtMeta().data?.data ?? null) as WtTicketCatalog | null;
  const ratesQuery = useWtRates(undefined, canWrite);
  const update = useUpdateWtTicket();
  const refresh = useRefreshWtTicket();
  const del = useDeleteWtTicket();
  const [reverted, setReverted] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [unpricedIds, setUnpricedIds] = useState<string[]>([]);
  const [voiding, setVoiding] = useState(false);
  const preflight = useRunWtAi();
  const [preflightResult, setPreflightResult] = useState<WtAiRunResult | null>(null);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const canPreflight = canUseWtAi(user?.role);
  const t = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const tz = meta?.timeZone ?? null;
  const failure = queryLoadFailure(query);
  const mode = wtTicketEditMode(t?.status);
  const editable = canWrite && (mode === 'free' || (mode === 'reverts' && unlocked));

  const onEdited = (r: WtTicketEditResult) => {
    if (r.reverted) {
      setReverted(true);
      setUnlocked(false);
    }
  };

  const openPdf = async () => {
    if (!t) return;
    setPdfError(null);
    const win = claimBlobTab();
    if (!win) return setPdfError(POPUP_BLOCKED_MESSAGE);
    setPdfBusy(true);
    try {
      const res = await api.wellTesting.tickets.pdf(t.id);
      showBlobInTab(win, res.data);
    } catch (e) {
      win.close();
      setPdfError(await getBlobApiErrorMessage(e, 'The PDF could not be made.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const runPreflight = () => {
    if (!t) return;
    setPreflightError(null);
    preflight.mutate(
      { agent: 'wt_ticket_preflight', ticketId: t.id },
      {
        onSuccess: (r) => setPreflightResult(r ?? null),
        onError: (e) => setPreflightError(wtAiRefusal(e, 'The preflight did not run.')),
      },
    );
  };

  return (
    <div data-testid="wt-ticket-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WT_EYEBROW}
        title={t?.ticketNumber || (query.isLoading ? 'Field ticket' : '—')}
        description={t ? `${t.customerName ?? '—'} · ${wtTicketWellText(t)} · ${t.jobNumber ?? '—'} · ${wtScopeText(t)}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          t ? (
            <div className="flex flex-wrap gap-2">
              {wtTicketHasPdf(t.status) && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={openPdf} disabled={pdfBusy}>
                  <FileText className="size-4" aria-hidden="true" />
                  {pdfBusy ? 'Opening…' : 'PDF'}
                </button>
              )}
              {canPreflight && wtTicketPreflightable(t.status) && (
                <WtPreflightButton busy={preflight.isPending} onClick={runPreflight} className={DH_BAND_SECONDARY} testId="wt-ticket-preflight" />
              )}
              {canWrite && t.status !== 'void' && (
                <button type="button" className={DH_BAND_SECONDARY} data-testid="wt-ticket-void-open" onClick={() => setVoiding(true)} disabled={voiding}>
                  <Ban className="size-4" aria-hidden="true" />
                  Void
                </button>
              )}
              {canWrite && wtTicketDeletable(t.status) && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WtLoading label="Loading ticket" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !t) ? (
            <WtPanel eyebrow="Field ticket" title="Field ticket">
              <WtEmpty>This ticket was not found. It may have been deleted.</WtEmpty>
            </WtPanel>
          ) : failure || !t ? (
            <WtPanel eyebrow="Field ticket" title="Field ticket">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this ticket" />
            </WtPanel>
          ) : (
            <>
              <WtFormError message={pdfError} />
              <WtFormError message={preflightError} />
              {preflightResult && (preflightResult.data as { ticket?: { id?: string } } | null)?.ticket?.id === t.id && (
                <WtAiRunInPlace result={preflightResult} canReview={canPreflight} />
              )}
              {voiding && canWrite && t.status !== 'void' && <VoidTicket ticket={t} onCancel={() => setVoiding(false)} onDone={() => setVoiding(false)} />}
              {reverted && <WtRevertedNotice onDismiss={() => setReverted(false)} />}
              {deleting && (
                <WtInlineConfirm
                  testId="wt-ticket-delete-confirm"
                  message={`Delete ${t.ticketNumber}? Its lines go with it. The job's shift logs are not touched, so it can be drafted again.`}
                  confirmLabel="Delete ticket"
                  cancelLabel="Keep it"
                  pending={del.isPending}
                  error={deleteError}
                  onCancel={() => setDeleting(false)}
                  onConfirm={() =>
                    del.mutate(t.id, {
                      onSuccess: () => {
                        toast.success(`${t.ticketNumber} deleted`);
                        navigate('/well-testing/tickets', { replace: true });
                      },
                      onError: (e) => setDeleteError(wtTicketRefusal(e, 'The ticket could not be deleted.')),
                    })
                  }
                />
              )}
              {canWrite && mode === 'reverts' && !unlocked && (
                <div className="flex flex-wrap items-center gap-3">
                  {unlocking ? (
                    <WtInlineConfirm
                      testId="wt-ticket-unlock-confirm"
                      message="Editing this ticket returns it to draft: the office approval is withdrawn, and any review link sent to the customer stops working. It has to be approved and sent again."
                      confirmLabel="Unlock to edit"
                      cancelLabel="Leave it signed"
                      onCancel={() => setUnlocking(false)}
                      onConfirm={() => { setUnlocked(true); setUnlocking(false); }}
                    />
                  ) : (
                    <button type="button" className={WT_SECONDARY} onClick={() => setUnlocking(true)} data-testid="wt-ticket-unlock">
                      <Lock className="size-4" aria-hidden="true" />
                      Edit this ticket
                    </button>
                  )}
                </div>
              )}
              {mode === 'frozen' && (
                <p data-testid="wt-ticket-frozen" className="text-sm text-fieldiq-muted">
                  The customer has approved this ticket, so what it bills cannot change. Office notes can still be edited.
                </p>
              )}
              {mode === 'void' && (
                <p data-testid="wt-ticket-voided" role="status" className="rounded-fi-control border border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                  Void{t.voidedByName ? ` — voided by ${t.voidedByName}` : ''}
                  {t.voidedAt ? ` on ${formatInstant(t.voidedAt, tz) ?? '—'}` : ''}. Reason: {t.voidReason || '—'}. A void ticket bills nothing and
                  cannot be changed.
                </p>
              )}
              <WtPanel
                eyebrow="Field ticket"
                title="Details"
                testId="wt-ticket-details"
                actions={editable && !editingHeader ? <button type="button" className={WT_SECONDARY} onClick={() => { setHeaderError(null); setEditingHeader(true); }}>Edit details</button> : undefined}
              >
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WtSpec label="Status" value={<WtBadge skin={wtTicketStatusSkin(t.status)} value={t.status} />} />
                  <WtSpec label="Job" value={t.jobNumber ? <Link className="underline" to={`/well-testing/jobs/${t.jobId}?tab=tickets`}>{t.jobNumber}</Link> : '—'} mono />
                  <WtSpec label="Customer" value={t.customerName || '—'} />
                  <WtSpec label="Covers" value={wtScopeText(t)} />
                  <WtSpec label="Well" value={wtTicketWellText(t)} />
                  <WtSpec label="Province / state" value={t.provinceState || '—'} />
                  <WtSpec label="Currency" value={t.currency || '—'} />
                  <WtSpec label="Created" value={t.createdAt ? formatInstant(t.createdAt, tz) ?? '—' : '—'} />
                  {HEADER_FIELDS.map((f) => (
                    <WtSpec key={f.key} label={f.label} value={(t[f.read] as string | null) || '—'} />
                  ))}
                </dl>
                {t.customerNotes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{t.customerNotes}</p>}
                {editable && editingHeader && (
                  <HeaderForm
                    ticket={t}
                    pending={update.isPending}
                    error={headerError}
                    onCancel={() => setEditingHeader(false)}
                    onSubmit={(body) => {
                      if (!Object.keys(body).length) return setHeaderError('Nothing has changed.');
                      setHeaderError(null);
                      update.mutate({ id: t.id, body }, {
                        onSuccess: (r) => { toast.success('Details saved'); setEditingHeader(false); onEdited(r); },
                        onError: (e) => setHeaderError(wtTicketRefusal(e, 'The details could not be saved.')),
                      });
                    }}
                  />
                )}
              </WtPanel>
              {editable && (
                <div className="space-y-2">
                  {refreshing ? (
                    <WtInlineConfirm
                      testId="wt-ticket-refresh-confirm"
                      message={WT_REFRESH_WARNING}
                      confirmLabel="Refresh from the logs"
                      cancelLabel="Not now"
                      pending={refresh.isPending}
                      error={refreshError}
                      onCancel={() => setRefreshing(false)}
                      onConfirm={() =>
                        refresh.mutate(t.id, {
                          onSuccess: (r) => { toast.success('Lines redrawn from the shift logs'); setRefreshing(false); onEdited(r); },
                          onError: (e) => setRefreshError(wtTicketRefusal(e, 'The ticket could not be refreshed.')),
                        })
                      }
                    />
                  ) : (
                    <button type="button" className={WT_SECONDARY} data-testid="wt-ticket-refresh" onClick={() => { setRefreshError(null); setRefreshing(true); }}>
                      <RefreshCw className="size-4" aria-hidden="true" />
                      Refresh from the shift logs
                    </button>
                  )}
                </div>
              )}
              <WtTicketLines
                ticket={t}
                editable={editable}
                canWrite={canWrite}
                rates={ratesQuery.data?.data ?? []}
                catalog={catalog}
                highlight={unpricedIds}
                onEdited={onEdited}
              />
              {t.status === 'customer_approved' && <WtTicketInvoiceLink ticketId={t.id} customerId={t.customerId} />}
              <WtTicketSignOff ticket={t} canWrite={canWrite} tz={tz} today={meta?.today ?? null} onUnpriced={setUnpricedIds} />
              <InternalNotes ticket={t} canWrite={canWrite} />
            </>
          )}
        </div>
      </section>
    </div>
  );
}
